import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, InputNumber, Select, Tag, Row, Col, Statistic, message, Space, Tabs, Progress, Tooltip, Alert } from 'antd';
import { DollarOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, Cell } from 'recharts';
import apiService from 'services/api/apiService';

const fmt = (v: any) => `₦${parseFloat(v||0).toLocaleString('en-NG')}`;

const CompensationManagement: React.FC = () => {
  const [grades, setGrades] = useState<any[]>([]);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [gradeModal, setGradeModal] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [assignForm] = Form.useForm();
  const [evalModal, setEvalModal] = useState(false);
  const [editingGrade, setEditingGrade] = useState<any>(null);
  const [gradeForm] = Form.useForm();
  const [evalForm] = Form.useForm();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [gr, er, emr] = await Promise.all([
        apiService.getSalaryGrades(),
        apiService.getJobEvaluations(),
        apiService.getEmployees(),
      ]);
      if (gr.success) setGrades(gr.data || []);
      if (er.success) setEvaluations(er.data || []);
    } finally { setLoading(false); }
  };

  const handleGrade = async (values: any) => {
    const r = editingGrade
      ? await apiService.updateSalaryGrade({ ...values, id: editingGrade.id })
      : await apiService.createSalaryGrade(values);
    if (r.success) { message.success(editingGrade ? 'Grade updated' : 'Grade created'); setGradeModal(false); gradeForm.resetFields(); setEditingGrade(null); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleEval = async (values: any) => {
    const r = await apiService.createJobEvaluation(values);
    if (r.success) { message.success('Evaluation saved'); setEvalModal(false); evalForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const chartData = grades.map(g => ({ name: g.grade_code, min: g.min_salary/1000, mid: g.mid_salary/1000, max: g.max_salary/1000 }));

  const tabItems = [
    {
      key: 'grades',
      label: '📊 Salary Grades',
      children: (
        <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingGrade(null); gradeForm.resetFields(); setGradeModal(true); }}>Add Grade</Button>}>
          {chartData.length > 0 && (
            <Card style={{ marginBottom: 16 }} title="Salary Grade Bands (₦000s)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={v => `₦${v}k`} />
                  <ChartTooltip formatter={(v: any) => `₦${(v*1000).toLocaleString()}`} />
                  <Bar dataKey="min" fill="#93c5fd" name="Minimum" />
                  <Bar dataKey="mid" fill="#3b82f6" name="Midpoint" />
                  <Bar dataKey="max" fill="#1d4ed8" name="Maximum" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
          <Table dataSource={grades} rowKey="id" loading={loading}
            columns={[
              { title: 'Code', dataIndex: 'grade_code', render: (v: string) => <Tag color="blue">{v}</Tag> },
              { title: 'Grade Name', dataIndex: 'grade_name' },
              { title: 'Level', dataIndex: 'level' },
              { title: 'Minimum', dataIndex: 'min_salary', render: fmt },
              { title: 'Midpoint', dataIndex: 'mid_salary', render: fmt },
              { title: 'Maximum', dataIndex: 'max_salary', render: fmt },
              { title: 'Range', key: 'range', render: (_: any, r: any) => {
                const spread = ((r.max_salary - r.min_salary) / r.min_salary * 100).toFixed(0);
                return <Tag color="green">+{spread}%</Tag>;
              }},
              { title: 'Actions', render: (_: any, r: any) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingGrade(r); gradeForm.setFieldsValue(r); setGradeModal(true); }} />
                  <Button size="small" type="primary" onClick={() => { assignForm.setFieldsValue({ grade_code: r.grade_code }); setAssignModal(true); }}>Assign to Employee</Button>
                </Space>
              )},
            ]} />
        </Card>
      )
    },
    {
      key: 'evaluation',
      label: '⚖️ Job Evaluation',
      children: (
        <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setEvalModal(true)}>Evaluate Position</Button>}
          title="Job Evaluation — Hay Method (Skill, Responsibility, Effort, Working Conditions)">
          <Table dataSource={evaluations} rowKey="id" loading={loading}
            columns={[
              { title: 'Position', dataIndex: 'position_title' },
              { title: 'Department', dataIndex: 'department' },
              { title: 'Grade', dataIndex: 'grade_code', render: (v: string) => v ? <Tag color="blue">{v}</Tag> : '-' },
              { title: 'Skill', dataIndex: 'skill_score', render: (v: number) => <Progress percent={v} size="small" steps={5} /> },
              { title: 'Responsibility', dataIndex: 'responsibility_score', render: (v: number) => <Progress percent={v} size="small" steps={5} /> },
              { title: 'Total Score', dataIndex: 'total_score', render: (v: number) => <Tag color={v>=300?'red':v>=200?'orange':'green'}>{v}</Tag> },
              { title: 'Salary Range', key: 'range', render: (_: any, r: any) => r.min_salary ? `${fmt(r.min_salary)} — ${fmt(r.max_salary)}` : '-' },
            ]} />
        </Card>
      )
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>Compensation Management</h1>
      <Tabs items={tabItems} />

      {/* Grade Modal */}
      <Modal title={editingGrade ? 'Edit Salary Grade' : 'Create Salary Grade'} open={gradeModal} onCancel={() => { setGradeModal(false); gradeForm.resetFields(); }} footer={null} width={600}>
        <Form form={gradeForm} layout="vertical" onFinish={handleGrade}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="grade_code" label="Grade Code" rules={[{ required: true }]}><Input placeholder="e.g. GL-07" /></Form.Item></Col>
            <Col span={12}><Form.Item name="level" label="Level" rules={[{ required: true }]}><InputNumber min={1} max={20} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="grade_name" label="Grade Name" rules={[{ required: true }]}><Input placeholder="e.g. Senior Associate" /></Form.Item>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="min_salary" label="Minimum (₦)" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g,',')} parser={(v: any) => v!.replace(/,/g,'')} /></Form.Item></Col>
            <Col span={8}><Form.Item name="mid_salary" label="Midpoint (₦)" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g,',')} parser={(v: any) => v!.replace(/,/g,'')} /></Form.Item></Col>
            <Col span={8}><Form.Item name="max_salary" label="Maximum (₦)" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g,',')} parser={(v: any) => v!.replace(/,/g,'')} /></Form.Item></Col>
          </Row>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">{editingGrade ? 'Update' : 'Create'}</Button><Button onClick={() => setGradeModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Evaluation Modal */}
      <Modal title="Job Evaluation" open={evalModal} onCancel={() => { setEvalModal(false); evalForm.resetFields(); }} footer={null} width={600}>
        <Form form={evalForm} layout="vertical" onFinish={handleEval}>
          <Row gutter={16}>
            <Col span={16}><Form.Item name="position_title" label="Position Title" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="grade_code" label="Assigned Grade"><Select allowClear>{grades.map(g => <Select.Option key={g.grade_code} value={g.grade_code}>{g.grade_code} — {g.grade_name}</Select.Option>)}</Select></Form.Item></Col>
          </Row>
          <Form.Item name="department" label="Department"><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="skill_score" label="Skill Score (0-100)" rules={[{ required: true }]}><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="responsibility_score" label="Responsibility (0-100)" rules={[{ required: true }]}><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="effort_score" label="Effort (0-100)" rules={[{ required: true }]}><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="working_conditions_score" label="Working Conditions (0-100)" rules={[{ required: true }]}><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Save Evaluation</Button><Button onClick={() => setEvalModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>
      <Modal title="Assign Salary Grade to Employee" open={assignModal}
        onCancel={() => { setAssignModal(false); assignForm.resetFields(); }} footer={null} width={500}>
        <Alert message="This will update the employee's basic salary based on the selected grade and salary point." type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={assignForm} layout="vertical" onFinish={async (values) => {
          const r = await apiService.assignGradeToEmployee(values.employee_id, values.grade_code, values.salary_point);
          if (r.success) {
            message.success(r.message || 'Grade assigned and salary updated');
            setAssignModal(false); assignForm.resetFields(); fetchAll();
          } else message.error(r.error || 'Failed');
        }}>
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children">
              {employees.map((e: any) => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department} ({e.position})</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="grade_code" label="Salary Grade" rules={[{ required: true }]}>
            <Select>{grades.map((g: any) => <Select.Option key={g.grade_code} value={g.grade_code}>{g.grade_code} — {g.grade_name} (₦{Number(g.min_salary).toLocaleString()} – ₦{Number(g.max_salary).toLocaleString()})</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="salary_point" label="Salary Point" initialValue="mid" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="min">Minimum (Entry point)</Select.Option>
              <Select.Option value="mid">Mid-point (Standard)</Select.Option>
              <Select.Option value="max">Maximum (Top of grade)</Select.Option>
            </Select>
          </Form.Item>
          <Space><Button type="primary" htmlType="submit">Assign Grade & Update Salary</Button><Button onClick={() => setAssignModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>
    </div>
  );
};

export default CompensationManagement;
